import { z } from 'zod';
import { ContentDiscussion, ContentSignals } from './signals';
/**
 * The content types the Library reads (spec §6).
 *
 * `ContentBase` is spelled once and extended, mirroring the backend — a new
 * shared field is one edit here rather than five.
 */
export const ContentType = z.enum([
    'COURSE',
    'COLLECTION',
    'READING',
    'AUDIO',
    'PRINTABLE',
    'PODCAST',
    'QANDA',
    'PHRASE',
    'EVENT',
    'CASE_STUDY',
]);
export const Audience = z.enum(['PARENT', 'PRO', 'TSP', 'ORG']);
/**
 * The cohort half of an audience (TSP requests 2 and 4, 25 Sep 2026) — one
 * shape on rooms, events and content, so « Cohorte 2026 · Pod A » reads the
 * same everywhere. `cohortName` is the cohort's short name where one was
 * authored. A null `podId` is the whole cohort.
 */
export const CohortAudience = z.object({
    cohortId: z.string().uuid(),
    podId: z.string().uuid().nullable().default(null),
    cohortName: z.string(),
    podName: z.string().nullable().default(null),
});
/** What a client can tell about a translation that was borrowed (§6.3). */
export const LocalizationMeta = z.object({
    locale: z.string(),
    /** Fields filled from the source locale because this one lacked them. */
    fellBack: z.array(z.string()).default([]),
});
/*
 * Declared BEFORE `ContentBase`, which now carries an `author` of its own —
 * so every content type gets the byline the contract says it has, and the
 * three that declared it individually no longer need to.
 *
 * TypeScript's const bindings are not hoisted, and this has been moved three
 * times for that reason. Above `ContentBase` is above every consumer there
 * can be.
 */
export const ContentAuthor = z.object({
    id: z.string().uuid(),
    fullName: z.string(),
    credential: z.string().nullable().optional(),
});
export const ContentBase = z.object({
    id: z.string().uuid(),
    type: ContentType,
    status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']),
    premium: z.boolean(),
    destinationId: z.string().uuid().nullable(),
    authorId: z.string().uuid().nullable(),
    /**
     * Server enrichment whenever `authorId` is set, and the contract declares it
     * on EVERY content type — so it belongs here rather than on the three that
     * happened to need it first.
     *
     * Declaring it per type is how the Q&A card ended up with no host: the mock's
     * meta line is `Krysta Letto · Nov 25, 2025 · 52 min`, the server was sending
     * the name, and `QandA` had never been told the field existed. Same shape of
     * miss as `Podcast.host`, one level further up.
     */
    author: ContentAuthor.nullable().optional(),
    coverAssetId: z.string().uuid().nullable(),
    /**
     * A signed, short-lived URL — minted from the row we were allowed to read.
     *
     * **Null, never absent**, including when the asset exists but is not READY:
     * a cover whose upload was abandoned is not a cover, and a URL pointing at
     * nothing is worse than a field you can test.
     *
     * It EXPIRES (`MEDIA_URL_TTL_SECONDS`, 300 by default). Do not cache it past
     * the response and never persist it — §4.8.
     */
    coverUrl: z.string().nullable(),
    coverAlt: z.string().optional(),
    ageBandIds: z.array(z.string().uuid()).default([]),
    /**
     * The band to PRINT when only one fits — the design's "Ages 6–8" on a case
     * study card, where `ageBandIds` may hold several and a card has room for
     * one. Nullable, and every consumer falls back to printing nothing rather
     * than picking a band out of the array itself.
     */
    primaryAgeBandId: z.string().uuid().nullable().optional(),
    audiences: z.array(Audience),
    /**
     * The cohorts and pods that see it, beside `audiences`. Empty is « no
     * cohort » — the common case, and what an API predating the field means.
     */
    cohorts: z.array(CohortAudience).default([]),
    audienceIsDefault: z.boolean(),
    sourceLocale: z.string(),
    version: z.number().int().positive(),
    publishedAt: z.string().datetime().nullable(),
    meta: LocalizationMeta.optional(),
    /**
     * `premium` says a paid plan is REQUIRED — a property of the item.
     * `locked` says AND THIS READER DOES NOT HAVE ONE — a property of the
     * request. The two are different questions and only the server can answer
     * the second; deriving it from a subscriptions read would be a second answer
     * to a question this row already answers (§1.4).
     *
     * `locked: true` only ever arrives on a DIRECT READ BY ID. Discovery hides
     * premium items from an unentitled reader entirely, so a lock never needs
     * drawing inside a grid — anything in a list is already openable.
     *
     * Optional here only so a locale bundle or a fixture predating the field
     * still parses; the contract marks it required.
     */
    locked: z.boolean().optional(),
    /**
     * The opt-in pair, both filled by `?withSignals=true` on a LIST read.
     *
     * `null` means *you did not ask*, and it is the only thing that can mean
     * that — which is why the counts inside each are always present and often
     * zero. A gallery that wants the reaction row asks for it; every other read
     * pays nothing.
     *
     * Absent rather than null on reads that never carry them (a locale bundle, a
     * fixture predating the field), hence `.optional()` on top of `.nullable()`.
     */
    signals: ContentSignals.nullable().optional(),
    discussion: ContentDiscussion.nullable().optional(),
});
export const Reading = ContentBase.extend({
    type: z.literal('READING'),
    title: z.string(),
    summary: z.string().optional(),
    /**
     * Markdown, in the agreed vocabulary. Rendered by `<Markdown>`.
     *
     * **Null when `locked`** — the paid payload is withheld while the teaser
     * fields (title, summary, dek, anchorQuote, cover) are kept, precisely so a
     * paywall can be drawn instead of a dead page.
     */
    body: z.string().nullable(),
    /** The pull-quote. Its OWN field — not a marker inside the body. */
    anchorQuote: z.string().optional(),
    /** Standfirst. */
    dek: z.string().optional(),
    readingMinutes: z.number().int().nullable(),
});
export const AudioChapter = z.object({
    startsAtSeconds: z.number().int().nonnegative(),
    label: z.string(),
    position: z.number().nonnegative(),
});
export const Audio = ContentBase.extend({
    type: z.literal('AUDIO'),
    title: z.string(),
    summary: z.string().optional(),
    fileAssetId: z.string().nullable(),
    /**
     * The recording itself — a signed URL minted from THIS row's `fileAssetId`.
     *
     * Longer-lived than a cover (`MEDIA_PAYLOAD_URL_TTL_SECONDS`, 7200 by
     * default) because a member who pauses a twenty-minute meditation for lunch
     * must not come back to a dead player. Keyed on the FIELD, never the mime
     * type — a cohort submission is a document too, and about a real family.
     *
     * It is a BEARER CAPABILITY: anyone holding it reads those bytes until it
     * expires, with no session and no audience check. Safe to hand to an
     * `<audio src>`, which is what it is for. Never persist or log it.
     */
    fileUrl: z.string().nullable(),
    durationSeconds: z.number().int().nullable(),
    transcript: z.string().optional(),
    chapters: z.array(AudioChapter),
    /** True when the chapter list was borrowed from the source locale. */
    chaptersFellBack: z.boolean(),
});
export const Printable = ContentBase.extend({
    type: z.literal('PRINTABLE'),
    title: z.string(),
    summary: z.string().optional(),
    fileAssetId: z.string().nullable(),
    /** The PDF. Same bearer-capability rules as `Audio.fileUrl`. */
    fileUrl: z.string().nullable(),
    fileSizeBytes: z.number().int().nullable(),
    pageCount: z.number().int().nullable(),
});
/** A series, as it arrives embedded on an episode. */
/**
 * How long a Vimeo-hosted recording runs, in seconds.
 *
 * ── One declaration for two types, on purpose ────────────────────────────
 * `Podcast` and `QandA` both have it and the reasoning is identical, so the
 * backend collapsed its two docblocks into a single `VIMEO_DURATION` constant
 * `.describe()`d onto both fields. Two prose copies of the same rule stop
 * agreeing eventually; this is the same move on this side.
 *
 * **Authored, not fetched.** The recording lives on Vimeo, so there is no asset
 * row to stat — a figure the FILE knows is derived, a figure only a PERSON
 * knows stays authored. Asking Vimeo at write time would couple saving to their
 * availability and still answer nothing for a privacy-locked video, which this
 * platform has.
 *
 * **Entity-level, not per locale**, unlike `Audio.durationSeconds`: a French and
 * an English audio are two files of two lengths, but one Vimeo recording serves
 * every locale — the same reason these two types have no per-locale table.
 *
 * Null until somebody enters one, which is a chip that simply does not draw.
 */
const vimeoDuration = z.number().int().positive().nullable();
export const PodcastSeriesRef = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
});
export const PodcastSeries = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    description: z.string().optional(),
    position: z.number().nonnegative(),
    /** A hex the editor chose for the series band. Null means our default. */
    accent: z.string().nullable(),
    visible: z.boolean(),
    episodeCount: z.number().int().nonnegative(),
    sourceLocale: z.string(),
    version: z.number().int().positive(),
    meta: LocalizationMeta.optional(),
});
/**
 * A podcast episode.
 *
 * `vimeoRef`, not `videoRef` — the two names are the backend's, on two
 * different models, and normalising them here would put a rename in the one
 * layer whose job is to say what the wire actually carries.
 *
 * There is no `videoOk` on this model. A course module has an editor's
 * assertion that its video plays; an episode does not, so an unplayable
 * reference here is only visible when the embed fails.
 */
export const Podcast = ContentBase.extend({
    type: z.literal('PODCAST'),
    title: z.string(),
    summary: z.string().optional(),
    episodeNumber: z.number().int().positive().nullable(),
    /** Null when `locked` — the reference is not minted at all. */
    vimeoRef: z.string().nullable(),
    hostId: z.string().uuid().nullable(),
    /**
     * Server enrichment when `hostId` is not null — the same shape `Course` and
     * `Event` declare. It is in the contract and was not declared here, so the
     * podcast gallery could name a guest and never the host.
     */
    host: ContentAuthor.nullable().optional(),
    guestName: z.string().nullable(),
    /** A calendar day, `YYYY-MM-DD` — not a timestamp. */
    recordedOn: z.string().nullable(),
    durationSeconds: vimeoDuration,
    seriesId: z.string().uuid().nullable(),
    /** Embedded, so a gallery groups by series without a second request. */
    series: PodcastSeriesRef.nullable(),
});
/**
 * A recorded question and its answer.
 *
 * `anonymous` and `askedBy` are a pair, and the rule is one-way: when
 * `anonymous` is true the asker is never named regardless of what `askedBy`
 * holds. A member asked a question about their own child; the flag is the
 * promise made when they asked it.
 */
export const QandA = ContentBase.extend({
    type: z.literal('QANDA'),
    title: z.string(),
    summary: z.string().optional(),
    /** Null when `locked` — the reference is not minted at all. */
    vimeoRef: z.string().nullable(),
    presenterId: z.string().uuid().nullable(),
    recordedOn: z.string().nullable(),
    durationSeconds: vimeoDuration,
    anonymous: z.boolean(),
    askedBy: z.string().nullable(),
});
/** Enough of a member to render a shelf row without a second request. */
export const CollectionMember = z.object({
    id: z.string().uuid(),
    type: ContentType,
    /** The segment THIS API serves. Our route is looked up from it, never built. */
    path: z.string(),
    title: z.string(),
    summary: z.string().optional(),
    status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']),
    premium: z.boolean(),
    position: z.number().nonnegative(),
    coverAssetId: z.string().uuid().nullable(),
    coverUrl: z.string().nullable(),
});
export const Collection = ContentBase.extend({
    type: z.literal('COLLECTION'),
    title: z.string(),
    summary: z.string().optional(),
    position: z.number().nonnegative(),
    accent: z.string().nullable(),
    visible: z.boolean(),
    orderedFor: Audience,
    orderIsCustomised: z.boolean(),
    items: z.array(CollectionMember),
    /**
     * The curator, and the wide banner art.
     *
     * Both are in the contract and neither was declared here, so the Library's
     * collection card could show a title and a count and nothing else — the
     * design's "8 pieces · Krysta Letto" line was missing its second half for want
     * of a field the server was already sending.
     */
    heroUrl: z.string().nullable().optional(),
    heroAlt: z.string().optional(),
});
export const ModuleChapter = z.object({
    startsAtSeconds: z.number().int().nonnegative(),
    label: z.string(),
    position: z.number().nonnegative(),
});
/**
 * A module resource: either a printable reference or a file, never both.
 *
 * The backend expresses the exclusivity as a `z.union`, and warns that
 * `openapi-typescript` widens the forbidden key to `unknown` — so the generated
 * type enforces "never neither" and not "never both". This runtime schema is
 * what enforces the rest.
 */
export const ModuleResource = z.object({
    id: z.string().uuid(),
    /*
     * `label` is optional here even though most modules carry one.
     *
     * A resource is one of two shapes: a **printable reference**
     * (`printableId + fileUrl`), or a **file attachment**
     * (`fileAssetId + fileUrl + label`). Only the second names itself
     * — the printable-reference variant inherits its display text from
     * the target `Printable.title`, which the client resolves on
     * render. The backend therefore sends printable references with no
     * `label` field at all, and requiring one here made the whole
     * `/courses` response fail to parse the day a course shipped a
     * printable module resource — flipping the entire Library shelf to
     * "isn't loading" (reported live on dev).
     *
     * `position` is added for the same round-trip reason — the backend
     * carries it and Zod stripped it silently otherwise; leaving it
     * declared means a client that reorders resources can round-trip
     * the same shape it read.
     */
    label: z.string().optional(),
    position: z.number().nonnegative().optional(),
    printableId: z.string().uuid().nullable().optional(),
    fileAssetId: z.string().uuid().nullable().optional(),
    /** Resolvable since round 5 — a module's worksheet is downloadable. */
    fileUrl: z.string().nullable().optional(),
});
export const CourseModule = z.object({
    id: z.string().uuid(),
    position: z.number().nonnegative(),
    /**
     * A Vimeo reference — URL or id. Vimeo is the only video provider on this
     * platform, which the contract now states outright, so there is no
     * discriminator to branch on.
     */
    videoRef: z.string().nullable(),
    /**
     * The EDITOR'S assertion that the video plays — checked by a person, never by
     * the API. Nothing resolves the reference. We surface it as "this module is
     * not ready" rather than as a technical failure, because that is what it is.
     */
    videoOk: z.boolean(),
    durationSeconds: z.number().int().nullable(),
    title: z.string(),
    summary: z.string().optional(),
    transcript: z.string().optional(),
    chapters: z.array(ModuleChapter),
    resources: z.array(ModuleResource),
    state: z.enum(['Video missing', 'Incomplete', 'Ready']),
});
/**
 * A short byline object the backend enriches when `authorId` is set.
 *
 * `credential` is optional — the seed doesn't yet carry it on every author,
 * and the UI degrades to `fullName` alone rather than rendering a dangling
 * dot with nothing after it.
 */
export const Course = ContentBase.extend({
    type: z.literal('COURSE'),
    title: z.string(),
    summary: z.string().optional(),
    modules: z.array(CourseModule),
    /**
     * Anchor phrase and walkaway bullets are being added on the wire in parallel;
     * declaring them here now makes the deploy safe on both sides — a response
     * that arrives without them still parses (both are `.optional()` / `.default`),
     * and a response that arrives with them flows straight to the UI with no
     * follow-up client bump.
     */
    anchorQuote: z.string().nullable().optional(),
    walkaway: z.array(z.string()).default([]),
});
// ── Taxonomy, for the filter dropdowns ──────────────────────────────────────
export const Destination = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    label: z.string(),
    description: z.string().optional(),
    colour: z.string(),
    position: z.number().nonnegative(),
    /**
     * Who this term is for. **Empty means unrestricted**, not "nobody" — the
     * opposite of the same-named field on `OnboardingQuestion`, and both
     * readings are deliberate: a label is harmless everywhere, a question is
     * addressed to somebody.
     *
     * Never filtered here. `?audience=` on the read is what narrows the list,
     * and the server owns which terms an audience may see; re-deciding it in the
     * client is how two definitions of "for practitioners" end up disagreeing.
     */
    audiences: z.array(Audience),
});
export const AgeBand = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    label: z.string(),
    minMonths: z.number().int(),
    // `null` on an open-ended top band ("Teens" → 156+ months, no upper bound).
    // The previous non-null schema failed contract validation for the whole
    // `/age-bands` response, which took down every consumer (best-for chips
    // rendered `—`, crew-fit labelled every child WRONG AGE).
    maxMonths: z.number().int().nullable(),
    position: z.number().nonnegative(),
});
/** The API's `localised` envelope — one of three, and they are not interchangeable. */
export const localisedOf = (item) => z.object({ items: z.array(item), locale: z.string() });
//# sourceMappingURL=contracts.js.map